import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";

const POLL_INTERVAL_MS = 60_000;

/**
 * Sits in the AppShell header. Polls notification count every minute. When
 * unread > 0 the bell badge pulses softly to draw the eye without strobing.
 *
 * Compact icon-only on mobile (≤640px), icon + "Notifications" label on
 * tablet+. 44px touch target enforced via min-h/min-w-[44px].
 */
export function NotificationBell() {
  const [unread, setUnread] = useState<number>(0);

  async function refresh() {
    try {
      const res = await api.listNotifications();
      setUnread(res.unread_count);
    } catch {
      // ignore — bell stays at last known count
    }
  }

  useEffect(() => {
    void refresh();
    const t = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, []);

  const hasUnread = unread > 0;

  return (
    <Link
      to="/notifications"
      aria-label={
        hasUnread ? `Notifications, ${unread} unread` : "Notifications"
      }
      className={`relative inline-flex min-h-[40px] min-w-[40px] items-center justify-center
                  gap-1.5 rounded-md border border-ink-3 bg-ink-2 px-2 py-1.5
                  text-sm font-medium text-fg-1
                  transition-all duration-200 ease-out
                  hover:border-ink-4 hover:bg-ink-3 hover:text-fg-0
                  ${hasUnread ? "text-accent border-accent/40" : ""}`}
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
        <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
      </svg>
      <span className="hidden sm:inline">Notifications</span>
      {hasUnread && (
        <span
          className="absolute -right-1.5 -top-1.5 inline-flex min-w-[18px] h-[18px]
                     items-center justify-center rounded-full
                     bg-accent-bad px-1 text-[10px] font-semibold text-ink-0
                     ring-2 ring-ink-0
                     motion-safe:animate-live-pulse"
          aria-hidden="true"
        >
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
  );
}
